/**
 * Estado da execução em `localStorage`.
 *
 * O contexto real é interrupção constante: atender uma ligação, travar a
 * tela, trocar de app. Voltar tem que retomar exatamente onde estava. É o
 * requisito que mais influencia a percepção de qualidade desta tela.
 * Ver docs/specs/10-student-portal.md §34.
 */

export interface ExecutionState {
  workoutId: string
  sessionId: string | null
  startedAt: string
  currentIndex: number
  /** workoutExerciseId → séries marcadas */
  completedSets: Record<string, number>
  /** Instante em que o descanso começou; null = sem descanso ativo. */
  restStartedAt: string | null
  restSeconds: number
}

function key(slug: string, workoutId: string): string {
  return `steingym.execucao.${slug}.${workoutId}`
}

export function readExecution(slug: string, workoutId: string): ExecutionState | null {
  try {
    const raw = localStorage.getItem(key(slug, workoutId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as ExecutionState
    return parsed.workoutId === workoutId ? parsed : null
  } catch {
    return null
  }
}

export function writeExecution(slug: string, state: ExecutionState): void {
  try {
    localStorage.setItem(key(slug, state.workoutId), JSON.stringify(state))
  } catch {
    // Janela privada: a execução funciona, só não sobrevive ao fechamento.
  }
}

export function clearExecution(slug: string, workoutId: string): void {
  try {
    localStorage.removeItem(key(slug, workoutId))
  } catch {
    // nada a limpar
  }
}

// ── Fila offline ──────────────────────────────────────────────

export interface QueuedMutation {
  id: string
  endpoint: string
  method: 'POST' | 'PATCH' | 'DELETE'
  body: unknown
  createdAt: string
  attempts: number
}

const QUEUE_KEY = 'steingym.fila'
const MAX_QUEUE = 5
const MAX_ATTEMPTS = 5

/**
 * Fila genérica em localStorage, drenada na reconexão.
 *
 * Sem Background Sync API (suporte irregular no iOS), sem IndexedDB, sem
 * resolução de conflito. Resolve 100% do caso real: o aluno conclui o
 * treino sem rede. A forma é genérica de propósito, para receber outros
 * tipos de mutação sem redesenho. Ver docs/specs/11-pwa.md §11.
 */
export function enqueue(mutation: Omit<QueuedMutation, 'id' | 'createdAt' | 'attempts'>): void {
  try {
    const queue = readQueue()
    if (queue.length >= MAX_QUEUE) queue.shift()

    queue.push({
      ...mutation,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      attempts: 0,
    })

    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue))
  } catch {
    // sem fila; a conclusão se perde, mas a UI já confirmou ao aluno
  }
}

export function readQueue(): QueuedMutation[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY)
    return raw ? (JSON.parse(raw) as QueuedMutation[]) : []
  } catch {
    return []
  }
}

function writeQueue(queue: QueuedMutation[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue))
  } catch {
    // ignorado
  }
}

/**
 * Drena a fila. Um 409 ("já concluído") REMOVE o item sem erro visível:
 * significa que a sincronização já aconteceu.
 */
export async function drainQueue(
  send: (mutation: QueuedMutation) => Promise<{ ok: boolean; status: number }>,
): Promise<{ sent: number; dropped: number }> {
  const queue = readQueue()
  if (queue.length === 0) return { sent: 0, dropped: 0 }

  const remaining: QueuedMutation[] = []
  let sent = 0
  let dropped = 0

  for (const mutation of queue) {
    try {
      const result = await send(mutation)

      if (result.ok || result.status === 409) {
        sent += 1
        continue
      }

      // 4xx que não é conflito não se resolve tentando de novo.
      if (result.status >= 400 && result.status < 500) {
        dropped += 1
        continue
      }

      const attempts = mutation.attempts + 1
      if (attempts >= MAX_ATTEMPTS) dropped += 1
      else remaining.push({ ...mutation, attempts })
    } catch {
      const attempts = mutation.attempts + 1
      if (attempts >= MAX_ATTEMPTS) dropped += 1
      else remaining.push({ ...mutation, attempts })
    }
  }

  writeQueue(remaining)
  return { sent, dropped }
}

/**
 * Segundos restantes de descanso, calculados por Date.now().
 *
 * NUNCA por setInterval acumulado: com a tela bloqueada o navegador
 * suspende o interval e um contador que atrasa 40 segundos destrói a
 * confiança. Ao voltar, o tempo está correto.
 */
export function remainingRest(restStartedAt: string | null, restSeconds: number): number {
  if (!restStartedAt) return 0
  const elapsed = (Date.now() - new Date(restStartedAt).getTime()) / 1000
  return Math.max(0, Math.ceil(restSeconds - elapsed))
}
